import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';
import { createActor, fromPromise } from 'xstate';

import { Button } from '@/components/primitives/Button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/primitives/Popover';
import { useTranslation } from '@/hooks/useTranslation';
import { useMangroveSuitability } from '@/hooks/useUserFlag';
import { mapViewFrom } from '@/hooks/useViewChangeEvents';
import { onboardingQuery, useAuthStore } from '@/modules/auth';
import { useLanguageStore } from '@/modules/core';
import { RESTORATION_TOUR_DISPLAY } from '@/modules/tour/config/restorationTourSteps';
import { useTourAnchor } from '@/modules/tour/hooks/useTourAnchor';
import {
  RESTORATION_TOUR_ID,
  RESTORATION_TOUR_STEPS,
  restorationTourMachine,
} from '@/modules/tour/machines/restorationTour';
import {
  markTourDone,
  recordTourOutcome,
  tourRecordsQuery,
} from '@/modules/tour/services/tourProgress';
import { appEvents } from '@/services/appEvents';

import { TourSpotlight } from './TourSpotlight';
import { TourStepCard } from './TourStepCard';

import type { PopoverSide } from '@/components/primitives/Popover';
import type { TourStepDisplay } from '@/modules/tour/config/restorationTourSteps';
import type { TourEligibility } from '@/modules/tour/machines/restorationTour';
import type { RefObject } from 'react';
import type { Actor } from 'xstate';

const MIRRORED: Partial<Record<PopoverSide, PopoverSide>> = { left: 'right', right: 'left' };

/** Steps after the welcome, counted in the card's step line and dots. */
const PROGRESS_STEPS = RESTORATION_TOUR_STEPS.filter(
  (step) => !RESTORATION_TOUR_DISPLAY[step].welcome
);

type TourActor = Actor<typeof restorationTourMachine>;

const noSubscription = () => () => {};

function useTourSnapshot(actor: TourActor | null) {
  return useSyncExternalStore(
    useMemo(
      () =>
        actor
          ? (onChange: () => void) => {
              const subscription = actor.subscribe(onChange);
              return () => subscription.unsubscribe();
            }
          : noSubscription,
      [actor]
    ),
    () => actor?.getSnapshot() ?? null
  );
}

/**
 * Hosts the restoration tour: supplies the machine's eligibility and storage,
 * forwards app events to it, and renders its current step. Mounted once per
 * session so the tour outlives route changes.
 */
export function TourController() {
  const { t } = useTranslation();
  const userId = useAuthStore((state) => state.user?.id);
  const queryClient = useQueryClient();
  const hasMangroveSuitability = useMangroveSuitability();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const view = mapViewFrom(pathname, searchParams);

  // Read when the machine runs, so the actor is never recreated for these.
  const latest = useRef({ view, hasMangroveSuitability });
  useLayoutEffect(() => {
    latest.current = { view, hasMangroveSuitability };
  });

  const [actor, setActor] = useState<TourActor | null>(null);

  // Layout effect: listening before any sibling's effect can emit.
  useLayoutEffect(() => {
    if (userId == null) return;

    const tourActor = createActor(
      restorationTourMachine.provide({
        actors: {
          loadEligibility: fromPromise<TourEligibility>(async () => {
            const onboarding = await queryClient.fetchQuery(onboardingQuery(userId));
            const eligibility: TourEligibility = {
              isFreeTier: onboarding.is_free_tier,
              hasMangroveSuitability: latest.current.hasMangroveSuitability,
              alreadyDone: false,
            };
            if (!eligibility.isFreeTier || !eligibility.hasMangroveSuitability) return eligibility;
            const records = await queryClient.fetchQuery(tourRecordsQuery(userId));
            return { ...eligibility, alreadyDone: !!records.tours[RESTORATION_TOUR_ID] };
          }),
        },
        actions: {
          markDone: () => {
            markTourDone(RESTORATION_TOUR_ID)
              .then((tours) =>
                queryClient.setQueryData(tourRecordsQuery(userId).queryKey, { exists: true, tours })
              )
              // Unsaved, the tour is offered again next visit.
              .catch((error: unknown) =>
                console.error('Could not save that the tour is done', error)
              );
          },
        },
      }),
      { input: { view: latest.current.view } }
    );

    // The tour's own announcements are for other listeners.
    const unsubscribe = appEvents.subscribe((event) => {
      if (!event.type.startsWith('tour.')) tourActor.send(event);
    });
    // Unrecorded, the outcome is only missing from the activity report.
    const recordOutcome = (outcome: Promise<void>) =>
      outcome.catch((error: unknown) =>
        console.error('Could not record how the tour ended', error)
      );
    const unsubscribeOutcome = appEvents.subscribe((event) => {
      if (event.type === 'tour.completed') {
        void recordOutcome(recordTourOutcome(event.payload.tour, 'completed'));
      } else if (event.type === 'tour.skipped') {
        void recordOutcome(recordTourOutcome(event.payload.tour, 'skipped', event.payload.step));
      }
    });
    tourActor.start();
    setActor(tourActor);

    return () => {
      unsubscribe();
      unsubscribeOutcome();
      tourActor.stop();
      setActor(null);
    };
  }, [userId, queryClient]);

  const snapshot = useTourSnapshot(actor);
  const step = snapshot?.matches('touring') ? snapshot.context.step : null;
  const display = step ? RESTORATION_TOUR_DISPLAY[step] : null;
  const visible = !!display && (display.view === 'any' || display.view === view);

  return (
    <>
      {/* Announces each step as it appears, for screen readers. */}
      <div aria-live="polite" className="sr-only">
        {visible ? `${t(display.title)}. ${t(display.body)}` : ''}
      </div>
      {actor && step && display && visible && (
        <TourStep
          key={step}
          display={display}
          stepNumber={display.welcome ? undefined : PROGRESS_STEPS.indexOf(step) + 1}
          canGoBack={!!snapshot?.can({ type: 'tour.back' })}
          onNext={() => actor.send({ type: 'tour.next' })}
          onBack={() => actor.send({ type: 'tour.back' })}
          onSkip={() => actor.send({ type: 'tour.skip' })}
        />
      )}
    </>
  );
}

interface TourStepProps {
  display: TourStepDisplay;
  stepNumber: number | undefined;
  canGoBack: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

function TourStep({ display, stepNumber, canGoBack, onNext, onBack, onSkip }: TourStepProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const anchor = useTourAnchor(display.placement === 'card' ? undefined : display.anchor);
  const virtualRef = useMemo(() => ({ current: anchor }), [anchor]);

  useEffect(() => {
    if (display.placement !== 'popover' || !anchor) return;
    anchor.setAttribute('data-tour-active', '');
    // The panel can't be scrolled by hand while the tour holds the screen.
    anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return () => anchor.removeAttribute('data-tour-active');
  }, [anchor, display.placement]);

  // A press outside the step asks whether to end the tour, rather than doing nothing.
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const showEndPrompt = () => setConfirmingEnd(true);
  const endPrompt =
    confirmingEnd &&
    createPortal(
      <div
        role="dialog"
        aria-label={t('End the tour?')}
        className="fixed bottom-6 left-1/2 z-50 w-[440px] max-w-[calc(100%-2rem)] -translate-x-1/2 rounded-2xl border border-outline bg-surface p-6 shadow-[0px_4px_16px_rgba(0,0,0,0.12)]"
      >
        <p className="text-lg font-semibold leading-6 text-foreground">{t('End the tour?')}</p>
        <p className="mt-2 text-sm text-foreground-muted">
          {t('You can restart it any time from Take the tour in your account menu.')}
        </p>
        <div className="mt-6 flex gap-2">
          <Button
            appearance="outline"
            kind="neutral"
            scale="compact"
            type="button"
            onClick={onSkip}
          >
            {t('End tour')}
          </Button>
          <Button
            appearance="solid"
            kind="inverse"
            scale="compact"
            type="button"
            onClick={() => setConfirmingEnd(false)}
          >
            {t('Keep going')}
          </Button>
        </div>
      </div>,
      document.body
    );

  const isCard = display.placement === 'card';
  const card = (
    <TourStepCard
      display={display}
      stepNumber={stepNumber}
      stepCount={PROGRESS_STEPS.length}
      canGoBack={canGoBack}
      // Cards interrupt, so they take focus; popovers point beside a control.
      focusOnMount={isCard}
      onNext={onNext}
      onBack={onBack}
      onSkip={onSkip}
    />
  );

  if (isCard) {
    return (
      <>
        <TourSpotlight target={null} onBlockedClick={showEndPrompt} />
        {createPortal(
          <div
            role="dialog"
            aria-modal="false"
            aria-label={t(display.title)}
            className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-outline bg-surface shadow-[0px_4px_16px_rgba(0,0,0,0.12)]"
          >
            {card}
          </div>,
          document.body
        )}
        {endPrompt}
      </>
    );
  }

  if (!anchor) return null;

  const side = display.side ?? 'bottom';
  return (
    <>
      <TourSpotlight target={anchor} onBlockedClick={showEndPrompt} />
      <Popover open>
        <PopoverAnchor virtualRef={virtualRef as RefObject<HTMLElement>} />
        <PopoverContent
          aria-label={t(display.title)}
          side={isRTL ? (MIRRORED[side] ?? side) : side}
          sideOffset={12}
          showArrow
          updatePositionStrategy="always"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="w-[340px] rounded-2xl p-0"
        >
          {card}
        </PopoverContent>
      </Popover>
      {endPrompt}
    </>
  );
}
